import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { KeyFilterModule } from 'primeng/keyfilter';
import { MessageService } from 'primeng/api';
import { debounceTime, distinctUntilChanged, filter, map, Subject, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { WarningResult } from '../../../models/validation-warning.model';
import { FormSchema, FormControlSchema } from '../../../models/form-schema.model';
import { EnvironmentService } from '../../../services/common/environment.service';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { handleDateInputChange, getFieldMaxLength, parseFlexibleDate } from '../../../utils/helpers';
import {
  FORM_AUTOSAVE_DEBOUNCE_MS,
  WARNING_EVALUATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { WarningMessageComponent, WarningMessage } from "../../common/warning-message/warning-message.component";
import {
  ROLE4_DIRECTOR_MAPPINGS,
  ROLE4_IDENTIFICATION_PATTERN,
  role4TypeRequiresPattern,
  ROLE4_NUMBER_OF_DIRECTORS_KEY,
  getVisibleDirectorCount,
} from '../../../services/mbrs/validators/role4-directors-report.rules';
import {
  canonicalizeIdentificationTypes,
  identificationNumberMaxLength,
  identificationTypeFieldFor,
  isNumericOnlyIdentificationField,
} from '../../../utils/identification-field.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { finalizeSheetInit } from '../../../utils/sheet-init-finalize.util';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { SelectClearIconBehaviorDirective } from '../../../directives/select-clear-icon-behavior.directive';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import {
  computedControlPredicate,
  revealValidationErrors,
  syncRevealToValidity,
} from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchJsonAsset,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
/**
 * The dropdown that drives how many directors' blocks are shown. Aliases the
 * rules module's key rather than repeating the literal, so the component, the
 * pure validator and the submit projector cannot read different fields.
 */
const ROLE4_DIRECTOR_COUNT_QNAME = ROLE4_NUMBER_OF_DIRECTORS_KEY;

/**
 * The FIRST director's name used to be the one director-scoped field the schema
 * left untagged. `role-120000.form.json` now carries `ui.directorNumber: 1` on
 * it (2026-07-29) — the in-component patch below is kept only as a belt-and-
 * braces fallback for that one qname, because the untagged shape ALSO made the
 * pure validator treat the field as unconditionally required and the submit
 * projector refuse to strip it. `directorNumberOf` short-circuits on the schema
 * tag, so this constant is inert while the tag is present.
 */
const ROLE4_FIRST_DIRECTOR_NAME_QNAME = 'ssmt:NameOfFirstDirectorWhoSignedDirectorsReport';

@Component({
  selector: 'app-role4-120000-directors-report',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Select,
    SelectClearIconBehaviorDirective,
    ButtonModule,
    DatePicker,
    TooltipModule,
    Toast,
    KeyFilterModule,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    WarningMessageComponent,
    PlaceholderPipe,
    FixedPlaceholderPipe
  ],
  providers: [MessageService],
  templateUrl: './role4-120000-directors-report.component.html',
  styleUrls: ['./role4-120000-directors-report.component.css']
})
export class Role4120000DirectorsReportComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);

  /** Check if the current environment is development (for showing debug info like role IDs) */
  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  directorsReportForm!: FormGroup;
  schema!: FormSchema;
  typeDict: any = {};
  isLoading = true;

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};

  // Dropdown options
  dropdownOptions: { [key: string]: any[] } = {};

  // Flag to track if we're loading saved state (to prevent auto-save during load)
  private isLoadingState = false;

  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint blanks a date the user is half-way through typing.
   * Mirrors role23/role24, which carry the identical listener and guard.
   */
  private isInitialLoadComplete = false;

  // Dynamic form title with Type of Submission
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Directors Report (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.directorsReport
    );
  }

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '4',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      await this.loadSchema();
      await this.loadTypeDict();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupStateListener();
      this.setupWarningEvaluation(); // Evaluate warnings on value changes
      this.setupConditionalValidators(); // Setup conditional pattern validators
      this.setupCrossSheetWarningListener(); // Listen for auditor's report changes
      this.evaluateFieldWarnings(); // Evaluate warnings on initial load

      // Deterministic finalization (replaces the legacy chained
      // setTimeout(100 → 600) that used to live in loadSavedState).
      // Extracted to finalizeInit() so the non-init caller
      // (onCancelConfirmed) can re-finalize after its own
      // loadSavedState() call — see finalizeInit's doc comment.
      await this.finalizeInit();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Deterministic sheet-init finalization. Must run after EVERY loadSavedState()
   * (init AND cancel/revert) — the legacy setTimeout lived inside loadSavedState
   * and re-finalized on each call. Safe: all init-time patches use emitEvent:false.
   *
   * `persistInitial` re-baselines the transformed form value: loadSavedState
   * patches the form with `emitEvent:false` AFTER applying load transforms
   * (identification-type casing canonicalization, date parsing, and the
   * rich-text field's on-load fragment normalization). Those transformed values
   * never reach `modifiedState` on their own, so without this the baseline
   * snapshot captured the RAW pre-transform shape and the user's first edit
   * persisted the transformed shape — a permanent modified-vs-original diff that
   * kept the sheet-selector pencil lit even after a full revert. Pushing
   * `form.value` (matching the auto-save persist) with `skipEditedMark=true`
   * makes `original` equal what the form persists. It DID re-run
   * evaluateFieldWarnings() after the isLoadingState flip; that is preserved
   * here after the helper resolves.
   */
  private async finalizeInit(): Promise<void> {
    await finalizeSheetInit({
      trace: this.loadTraceHandle,
      stateService: this.stateService,
      sheetKey: SHEET_KEYS.directorsReport,
      form: this.directorsReportForm,
      setLoadingState: (isLoading) => { this.isLoadingState = isLoading; },
      isComputed: computedControlPredicate(this.schema?.controls),
      persistInitial: () => {
        this.stateService.updateSheetData(
          SHEET_KEYS.directorsReport,
          this.directorsReportForm.value,
          this.directorsReportForm.valid,
          true, // skipEditedMark — re-baseline, not a user edit
        );
      },
    });

    // Preserved from the legacy setTimeout block: re-evaluate warnings once
    // saved state is loaded and auto-save is re-enabled.
    this.evaluateFieldWarnings();

    // Initial load has settled — from here the state listener stops re-patching.
    this.isInitialLoadComplete = true;
  }

  /**
   * THE single reader for "how many directors are currently selected".
   *
   * Returns `ROLE4_NO_DIRECTOR_COUNT_SELECTED` (0) whenever the dropdown is
   * cleared (`null`, `''`) or holds something unparseable, so every consumer —
   * visibility (`shouldShowField`), validators (`updateDirectorFieldValidators`,
   * `applyConditionalValidator`) and the change watcher — agrees that a cleared
   * count means ZERO visible directors rather than the old hardcoded default 2.
   *
   * This replaced five copies of `value ? parseInt(value, 10) : 2`; add new
   * director-count reads here, not at the call site.
   *
   * DELEGATES to the rules module's `getVisibleDirectorCount` — the same
   * function the pure validator and the submit projector call — so the parse
   * and its empty/NaN handling exist exactly once. A second local parser is how
   * the 2026-07-28 divergence (component 0, projector 2) got in.
   */
  private resolveDirectorCount(rawValue: unknown): number {
    return getVisibleDirectorCount({ [ROLE4_DIRECTOR_COUNT_QNAME]: rawValue });
  }

  /** Current director count straight off the form (0 when cleared). */
  private getSelectedDirectorCount(): number {
    return this.resolveDirectorCount(
      this.directorsReportForm?.get(ROLE4_DIRECTOR_COUNT_QNAME)?.value
    );
  }

  /**
   * Director this schema control belongs to, or `undefined` for a field that is
   * not director-scoped. Folds in the first director's name, which the schema
   * leaves untagged (see ROLE4_FIRST_DIRECTOR_NAME_QNAME).
   */
  private directorNumberOf(control: FormControlSchema): number | undefined {
    if (control.ui?.directorNumber !== undefined) {
      return control.ui.directorNumber;
    }
    return control.qname === ROLE4_FIRST_DIRECTOR_NAME_QNAME ? 1 : undefined;
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.directorsReport),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((directorsReportData) => {
        if (directorsReportData && Object.keys(directorsReportData).length > 0) {
          // After initial load completes, skip re-patching. `state$` is the
          // MODIFIED stream, so this sheet's own debounced persist comes
          // straight back here — and `patchValue` reaches PrimeNG's
          // `writeValue`, which re-renders the input from the model even under
          // `emitEvent: false`. While a date is half-typed that model is null
          // (`DatePicker.onUserInput` calls `updateModel(null)` on text its
          // `parseValueFromString` rejects), so the echo blanks the digits the
          // user just entered. Adopting a SIBLING TAB's values is
          // `adoptRemoteState()`'s job, on `remoteUpdateApplied$`.
          // Mirrors role12/role23/role24. See isInitialLoadComplete.
          if (this.isInitialLoadComplete) {
            return;
          }

          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // Convert date string to Date object for PrimeNG DatePicker
          // Uses parseFlexibleDate to handle both DD/MM/YYYY and ISO 8601 formats from backend
          const processedData = { ...directorsReportData };
          if (processedData['ssmt:DateOfSigningDirectorsReport']) {
            const parsed = parseFlexibleDate(processedData['ssmt:DateOfSigningDirectorsReport']);
            if (parsed) {
              processedData['ssmt:DateOfSigningDirectorsReport'] = parsed;
            }
          }

          // Canonicalize TypeOfIdentification values (case-insensitive snap to the enum)
          // so the dropdowns bind even when the source casing drifts (e.g. "MYKAD",
          // "Police ID Number"). Shared with role5 — see identification-field.util.
          const canonicalData = canonicalizeIdentificationTypes(
            processedData,
            this.typeDict?.enums?.['TypeOfIdentification'] ?? [],
          );

          // Patch the form with the new data
          this.directorsReportForm.patchValue(canonicalData, { emitEvent: false });

          // IMPORTANT: Re-apply validators based on number of directors from backend data
          // This ensures only visible director fields are required. Applied
          // UNCONDITIONALLY: when the incoming count is absent/cleared the
          // resolver yields 0, and the director fields must shed `required`
          // to match the fact that none of them are rendered.
          this.updateDirectorFieldValidators(
            this.resolveDirectorCount(
              directorsReportData[ROLE4_DIRECTOR_COUNT_QNAME]
            )
          );

          // Evaluate field warnings after data is loaded
          this.evaluateFieldWarnings();

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        }
      });
  }

  /**
   * Load the form schema from JSON
   */
  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-120000.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Load type dictionary for dropdown options
   */
  private async loadTypeDict(): Promise<void> {
    // FATAL by design: without the dictionary the dropdowns have no options and
    // the conditional mappings cannot run, so the sheet must not open as if it had
    // loaded. The typed error propagates to the sheet's single catch, which
    // reports ASSET / PARSE / NET through the load trace.
    this.typeDict = await fetchJsonAsset<any>('/assets/types.dict.json');
    this.buildDropdownOptions();
  }

  /**
   * Build dropdown options from type dictionary
   */
  private buildDropdownOptions(): void {
    if (!this.typeDict.enums) return;

    Object.keys(this.typeDict.enums).forEach(key => {
      this.dropdownOptions[key] = this.typeDict.enums[key].map((value: string) => ({
        label: value,
        value: value
      }));
    });
  }

  /**
   * Build the reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      const validators = [];

      // Add required validator
      if (control.required) {
        validators.push(Validators.required);
      }

      // Add the schema's declared maxLength for text controls. Read from the
      // schema rather than hardcoded, so the seated validator, the rendered
      // `maxlength` attribute and the pure validator all quote one number.
      const maxLength = getFieldMaxLength(control.qname, this.schema);
      if (maxLength !== null) {
        validators.push(Validators.maxLength(maxLength));
      }

      // Get default value
      let defaultValue = '';
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Create form control
      const formControl = new FormControl(
        { value: defaultValue, disabled: control.ui.readonly || false },
        validators
      );

      formControls[control.qname] = formControl;
    });

    this.directorsReportForm = new FormGroup(formControls);

    // Seat the director validators from the CURRENT count (0 on a fresh sheet),
    // so a director field is never both hidden and `required` — a blocking error
    // the user can neither see nor reach.
    this.updateDirectorFieldValidators(this.getSelectedDirectorCount());
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.directorsReport);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.directorsReport);

    if (savedData && Object.keys(savedData).length > 0) {
      // Convert date strings back to Date objects for PrimeNG DatePicker
      // Uses parseFlexibleDate to handle both DD/MM/YYYY and ISO 8601 formats from backend
      const processedData = { ...savedData };
      if (processedData['ssmt:DateOfSigningDirectorsReport']) {
        const parsed = parseFlexibleDate(processedData['ssmt:DateOfSigningDirectorsReport']);
        if (parsed) {
          processedData['ssmt:DateOfSigningDirectorsReport'] = parsed;
        }
      }

      // Canonicalize TypeOfIdentification values (case-insensitive snap to the enum)
      // so the dropdowns bind even when the persisted casing drifts. Shared with role5.
      const canonicalData = canonicalizeIdentificationTypes(
        processedData,
        this.typeDict?.enums?.['TypeOfIdentification'] ?? [],
      );

      this.directorsReportForm.patchValue(canonicalData, { emitEvent: false });

      // Apply validators based on loaded number of directors (0 when the saved
      // count is absent or cleared — hidden fields must not stay required).
      this.updateDirectorFieldValidators(
        this.resolveDirectorCount(savedData[ROLE4_DIRECTOR_COUNT_QNAME])
      );
    }

    // NOTE: the isLoadingState flip + warning re-evaluation + baseline snapshot
    // (markSheetInitializedWithStatus) are now owned by finalizeSheetInit(),
    // called deterministically from ngOnInit (and from onCancelConfirmed) via
    // finalizeInit() after waitForMbrsHydration(). We keep ONLY the synchronous
    // setup above (markSheetInitializing + the emitEvent:false converted patch
    // + validator application) here.
  }

  /**
   * Setup auto-save on form changes
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.directorsReportForm.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        const isValid = this.directorsReportForm.valid;
        this.stateService.updateSheetData(SHEET_KEYS.directorsReport, this.directorsReportForm.value, isValid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.directorsReport, { hasErrors: !this.directorsReportForm.valid });
      },
    });

    // Watch for changes in number of directors to manage field visibility
    this.setupDirectorFieldsWatcher();
  }

  /**
   * Setup watcher for number of directors field to manage dynamic field visibility
   */
  private setupDirectorFieldsWatcher(): void {
    const numberOfDirectorsControl = this.directorsReportForm.get(ROLE4_DIRECTOR_COUNT_QNAME);

    if (numberOfDirectorsControl) {
      numberOfDirectorsControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          const selectedNumber = this.resolveDirectorCount(value);

          // Prune UNCONDITIONALLY, including on a CLEARED count (the dropdown's
          // [showClear] X, or the Delete key it enables on PrimeNG's Select),
          // which resolves to 0 and therefore blanks EVERY director block.
          //
          // The 2026-07-28 revision skipped the prune on a cleared count so the
          // values survived a clear-then-reselect round trip. That was reversed
          // 2026-07-29: it made clearing behave differently from every other
          // downward change (5 -> 2 prunes directors 3-5; 5 -> cleared pruned
          // nothing) and left hidden values sitting in the persisted state. The
          // round-trip data loss is accepted — it is identical to what already
          // happens on 5 -> 2 -> 5, and hiding a field while keeping its value
          // is the leak this prune exists to prevent.
          this.clearHiddenDirectorFields(selectedNumber);

          // Update validators for director fields. ALWAYS runs, including on a
          // cleared count: the hidden director fields must shed `required` or
          // they block submission with an error the user cannot reach.
          this.updateDirectorFieldValidators(selectedNumber);
        });
    }
  }

  /**
   * Clear values for director fields that should be hidden
   */
  private clearHiddenDirectorFields(selectedNumber: number): void {
    this.schema.controls.forEach(control => {
      const directorNumber = this.directorNumberOf(control);
      if (directorNumber !== undefined && directorNumber > selectedNumber) {
        const formControl = this.directorsReportForm.get(control.qname);
        if (formControl && formControl.value) {
          formControl.setValue('', { emitEvent: false });
        }
      }
    });
  }

  /**
   * Update validators for director fields based on visibility
   */
  private updateDirectorFieldValidators(selectedNumber: number): void {
    this.schema.controls.forEach(control => {
      const directorNumber = this.directorNumberOf(control);
      if (directorNumber !== undefined) {
        const formControl = this.directorsReportForm.get(control.qname);
        if (formControl) {
          const isVisible = directorNumber <= selectedNumber;

          // For identification number fields, use conditional validator logic
          if (control.qname.includes('IdentificationNumber')) {
            const typeFieldQname = control.qname.replace('IdentificationNumber', 'TypeOfIdentification');
            const typeControl = this.directorsReportForm.get(typeFieldQname);
            if (typeControl) {
              // Apply conditional validator based on current type value
              this.applyConditionalValidator(typeControl.value, formControl as FormControl);
            } else {
              formControl.clearValidators();
            }
          } else if (control.qname.includes('Name') || control.qname.includes('TypeOfIdentification')) {
            // Name and Type fields are required — and capped — only while
            // visible. maxLength has to be re-applied because setValidators
            // replaces the whole list, so buildForm's cap would otherwise be
            // dropped the first time this runs. Scoping it to `isVisible`
            // matches `required`: a hidden director contributes nothing to the
            // verdict, and an error on an off-screen field is unreachable.
            const validators: ValidatorFn[] = [];
            if (isVisible) {
              validators.push(Validators.required);
              const maxLength = getFieldMaxLength(control.qname, this.schema);
              if (maxLength !== null) {
                validators.push(Validators.maxLength(maxLength));
              }
            }
            if (validators.length > 0) {
              formControl.setValidators(validators);
            } else {
              formControl.clearValidators();
            }
          } else {
            // Other fields: use schema's required flag
            if (isVisible && control.required) {
              formControl.setValidators([Validators.required]);
            } else {
              formControl.clearValidators();
            }
          }

          formControl.updateValueAndValidity({ emitEvent: false });
        }
      }
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.directorsReportForm.invalid) {
      this.directorsReportForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    const formData = this.directorsReportForm.value;

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Directors report has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Get dropdown options for a field
   */
  getDropdownOptions(enumSource?: string): any[] {
    if (!enumSource) return [];

    const match = enumSource.match(/^type:(.+)$/);
    if (match && match[1]) {
      return this.dropdownOptions[match[1]] || [];
    }

    return [];
  }

  /**
   * Check if a field should be shown based on dynamic conditions
   */
  shouldShowField(control: FormControlSchema): boolean {
    // If this is a director-specific field (including the first director's name,
    // which the schema leaves untagged — see directorNumberOf).
    const directorNumber = this.directorNumberOf(control);
    if (directorNumber !== undefined) {
      // A cleared count resolves to 0, so NO director block is shown — not even
      // the first one (product owner, 2026-07-28). Nothing stays behind the
      // hidden fields: the watcher blanks them as it hides them (2026-07-29), so
      // a hidden field can never leak a value into state or the payload.
      return directorNumber <= this.getSelectedDirectorCount();
    }
    // Show all non-director fields
    return true;
  }

  /**
   * Check if a field is required (considering visibility)
   */
  /**
   * Does this field's label get a `*`?
   *
   * Single-sourced through `isControlMandatory`. The `isVisible` gate is what
   * keeps an off-screen director slot unmarked — this sheet renders up to five
   * director trios and shows only as many as the director-count driver selects.
   *
   * Until 2026-08-11 the TEMPLATE bypassed this method and bound
   * `*ngIf="control.required"` directly, and the schema flagged only directors
   * 1–2 — so the `*` vanished from the 3rd director onward while
   * `role4-directors-report.validator.ts` still REQUIRED those fields for any
   * visible director (`isVisible && (control.required === true ||
   * isDirectorNameOrType)`). A preparer selecting four directors saw no `*` on
   * directors 3–4 and was blocked at submit by an error the label never hinted
   * at. Both halves were fixed together: directors 3–5 now carry `required` in
   * `role-120000.form.json`, and the template calls this method.
   */
  isFieldRequired(control: FormControlSchema): boolean {
    return isControlMandatory(control as any, {
      selfData: this.directorsReportForm?.getRawValue() ?? {},
      isVisible: (control) => this.shouldShowField(control as any),
    });
  }

  /**
   * Check if a field has an error
   */
  hasError(fieldName: string): boolean {
    const control = this.directorsReportForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Check if a required field is empty (for showing red border)
   * Returns true if the field is required and has empty/null/undefined value
   */
  isRequiredFieldEmpty(qname: string): boolean {
    const schemaControl = this.schema?.controls.find(control => control.qname === qname);
    if (!schemaControl?.required) {
      return false;
    }

    const control = this.directorsReportForm.get(qname);
    if (!control) {
      return false;
    }

    const value = control.value;
    // 0 is a valid value, only null, undefined, and empty string are considered empty
    return value === null || value === undefined || value === '';
  }

  /**
   * Get error message for a field (returns first error for backward compatibility)
   * Implements three-level error fallback system:
   * 1. Custom error from schema JSON (control.error.required or control.error.pattern)
   * 2. Label-based error (e.g., "Date of signing director's report is required")
   * 3. Generic fallback (e.g., "This field is required")
   */
  getErrorMessage(fieldName: string): string {
    const errors = this.getAllErrorMessages(fieldName);
    return errors.length > 0 ? errors[0] : '';
  }

  /**
   * Get ALL error messages for a field (for simultaneous display)
   * Returns an array of all applicable error messages
   */
  getAllErrorMessages(fieldName: string): string[] {
    const control = this.directorsReportForm.get(fieldName);
    if (!control) {
      return [];
    }

    const errorMessages: string[] = [];
    const schemaControl = this.schema?.controls.find(control => control.qname === fieldName);

    // Check if field has been touched or dirty
    if (!control.touched && !control.dirty) {
      return [];
    }

    // Handle required error
    if (control.errors?.['required']) {
      // Level 1: Custom error from JSON schema
      if (schemaControl?.error?.required) {
        errorMessages.push(schemaControl.error.required);
      } else if (schemaControl?.label) {
        // Level 2: Label-based error
        errorMessages.push(`${schemaControl.label} is required`);
      } else {
        // Level 3: Generic fallback
        errorMessages.push('This field is required');
      }
    }

    // Handle pattern error
    if (control.errors?.['pattern']) {
      // Level 1: Custom error from JSON schema
      if (schemaControl?.error?.pattern) {
        errorMessages.push(schemaControl.error.pattern);
      } else {
        // Check if there's a validator with a custom message
        const validator = schemaControl?.validators?.find(validator => validator.name === 'pattern');
        if (validator && (validator as any).message) {
          errorMessages.push((validator as any).message);
        } else if (schemaControl?.label) {
          // Level 2: Label-based error
          errorMessages.push(`${schemaControl.label} has invalid format`);
        } else {
          // Level 3: Generic fallback
          errorMessages.push('Invalid format');
        }
      }
    }

    // Handle maxlength error
    if (control.errors?.['maxlength']) {
      const maxLength = control.errors['maxlength'].requiredLength;
      const actualLength = control.errors['maxlength'].actualLength;
      // Level 1: Custom error from JSON schema
      if (schemaControl?.error?.['maxlength']) {
        errorMessages.push(schemaControl.error?.['maxlength']);
      } else if (schemaControl?.label) {
        // Level 2: Label-based error
        errorMessages.push(`${schemaControl.label} must not exceed ${maxLength} characters (currently ${actualLength})`);
      } else {
        // Level 3: Generic fallback
        errorMessages.push(`Must not exceed ${maxLength} characters (currently ${actualLength})`);
      }
    }

    // Special case: an EMPTY identification number under a numeric ID type must
    // still advertise the 12-digit rule. Angular raises no `pattern` error on an
    // empty control, so the required message would otherwise be the only line —
    // the user is told the field is mandatory but not what shape it must take.
    // Matching is case-insensitive (role4TypeRequiresPattern); role 5 renders the
    // same two lines from its own getAllErrorMessages.
    if (control.errors?.['required'] && fieldName.includes('IdentificationNumber')) {
      const typeFieldName = fieldName.replace('IdentificationNumber', 'TypeOfIdentification');
      const typeControl = this.directorsReportForm.get(typeFieldName);
      const typeValue = typeControl?.value;

      if (role4TypeRequiresPattern(typeValue)) {
        // Add pattern error message as additional requirement
        if (schemaControl?.error?.pattern && !errorMessages.includes(schemaControl.error.pattern)) {
          errorMessages.push(schemaControl.error.pattern);
        }
      }
    }

    // Handle other error types (extensible)
    if (control.errors) {
      Object.keys(control.errors).forEach(errorKey => {
        if (errorKey !== 'required' && errorKey !== 'pattern' && errorKey !== 'maxlength' && schemaControl?.error?.[errorKey]) {
          const errorMsg = schemaControl.error[errorKey];
          if (!errorMessages.includes(errorMsg)) {
            errorMessages.push(errorMsg);
          }
        }
      });
    }

    return errorMessages;
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.directorsReportForm.get(qname);
  }

  /**
   * Determine if an identification number field should be numeric-only.
   * Numeric only when the paired Type of Identification is MyKad/MyPR/MyKAS;
   * every other type is free text. Shared logic in identification-field.util.
   */
  shouldBeNumericOnly(fieldName: string): boolean {
    const typeValue = this.directorsReportForm.get(identificationTypeFieldFor(fieldName))?.value;
    return isNumericOnlyIdentificationField(fieldName, typeValue);
  }

  /**
   * Get max length for a field.
   *
   * For an *IdentificationNumber control the cap is conditional on the paired
   * Type of Identification (12 for the numeric MyKad/MyPR/MyKAS IDs, 255 free
   * text otherwise) — see identification-field.util. Every other field (incl.
   * text blocks) falls back to its schema-derived max length.
   */
  getMaxLength(fieldName: string): number | null {
    const typeValue = this.directorsReportForm.get(identificationTypeFieldFor(fieldName))?.value;
    return identificationNumberMaxLength(fieldName, typeValue)
      ?? getFieldMaxLength(fieldName, this.schema);
  }

  /**
   * Setup warning evaluation on form value changes
   * Evaluates warnings for fields that have warning rules defined in schema
   */
  private setupWarningEvaluation(): void {
    this.directorsReportForm.valueChanges
      .pipe(
        debounceTime(WARNING_EVALUATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });
  }

  /**
   * Setup listener for cross-sheet validation
   * Listens to auditorsReport and filingPrerequisites state changes to trigger warning re-evaluation
   * This ensures that:
   * - When the auditor's date changes, the director's date validation is updated
   * - When audit status changes, the conditional validation is re-evaluated
   */
  private setupCrossSheetWarningListener(): void {
    // Listen to auditorsReport changes (for auditor's date comparison)
    this.stateService.state$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.directorsReport)),
        map((state) => state.auditorsReport),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((auditorsReportData) => {
        // Re-evaluate warnings when auditors report data changes
        // This handles cross-sheet validation for director's date vs auditor's date
        if (auditorsReportData && !this.isLoadingState) {
          this.evaluateFieldWarnings();
        }
      });

    // Listen to filingPrerequisites changes (for audit status condition)
    this.stateService.state$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.directorsReport)),
        // BOTH filing-info slots. Role 0 (`filingPrerequisites`) is the editable
        // slot in Phase 1 ONLY; Phase 2 writes to role 1 (`filingInfo`) and
        // leaves the role-0 slot an empty stub, so watching role 0 alone never
        // fired here. Guarded by `npm run check:filing-info-watch`.
        map((state) => ({
          filingPrerequisites: state.filingPrerequisites,
          filingInfo: state.filingInfo,
        })),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((filingInfoSlices) => {
        // Re-evaluate warnings when the filing info (audit status) changes
        // This handles the conditional "Audited" check for the date comparison rule
        if (
          (filingInfoSlices.filingPrerequisites || filingInfoSlices.filingInfo) &&
          !this.isLoadingState
        ) {
          this.evaluateFieldWarnings();
        }
      });

    // ----------------------------------
    // Cross-tab safety net — when auditorsReport or filingPrerequisites is
    // mutated remotely (sibling tab edits), re-run evaluateFieldWarnings()
    // so the director's date warnings stay in sync. Redundant by design with
    // the state$ watches above (applyRemoteChange also calls
    // modifiedStateSubject.next), but guarantees re-validation even if the
    // hash watches are short-circuited (init guard, schema race, etc.).
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.directorsReport)),
        filter(() => !!this.schema && !!this.directorsReportForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role4-directorsReport',
          //   sourceSheetKey: sheetKey,
          // });
        }
        this.evaluateFieldWarnings();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // directorsReport slice, `applyRemoteChange` rehydrates the shared state
    // and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges).
    //
    // Deliberately a SEPARATE subscription from the cross-tab safety net
    // above, but that safety net does NOT filter on `changedSheets` — it has
    // no slice filter at all — so it fires on EVERY remote `modifiedState`
    // update, including one that changed THIS sheet's own slice. It is
    // registered first, so on an own-slice remote update it runs
    // `evaluateFieldWarnings()` once against the still-STALE, pre-adoption
    // form, then this subscription runs `adoptRemoteState()`. The end state
    // is correct; the intermediate evaluation between the two is
    // wrong-valued for one microtask. Deliberately NOT deduped — deduping
    // risks dropping the upstream-driven refresh the safety net exists for
    // (filingPrerequisites/auditorsReport slice changes that never touch
    // `changedSheets` for this sheet at all); see fix-wave I4.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.directorsReport)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.directorsReport)),
        filter(() => !!this.schema && !!this.directorsReportForm),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role4-directorsReport',
          // });
        }
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(this.directorsReportForm, this.stateService.getSheetData(SHEET_KEYS.directorsReport));
    this.evaluateFieldWarnings();
  }

  /**
   * Evaluate field warnings for all fields with warning rules
   * Called on initial load and on form value changes
   *
   * Structures cross-sheet data properly for FieldWarningService:
   * - Cross-sheet data nested under sheet keys (e.g., filingPrerequisites, auditorsReport)
   * - Current form values also available at top level for same-sheet validation
   */
  private evaluateFieldWarnings(): void {
    if (!this.schema || !this.directorsReportForm) {
      return;
    }

    // Get all form values for cross-field validations
    const allFormValues = this.directorsReportForm.getRawValue();

    // Get Role-7 (Auditors Report) values for cross-sheet comparisons
    const role7Values = this.stateService.getSheetData(SHEET_KEYS.auditorsReport);

    // Structure cross-sheet data properly for FieldWarningService.
    // The service expects: allValues[sheetKey][fieldQname] for cross-sheet
    // access. buildFilingInfoBagEntry() supplies the active filing-info slot
    // under both alias keys (filingPrerequisites + filingInfo) so phase-aware
    // resolution stays lossless across Phase 1 and Phase 2.
    // Also include flat values for same-sheet field access.
    const allValuesForWarnings = {
      ...allFormValues,
      ...this.stateService.buildFilingInfoBagEntry(),
      auditorsReport: role7Values || {}
    };

    // Evaluate warnings for each field that has warning rules
    this.schema.controls.forEach((control) => {
      if (control.warnings && control.warnings.length > 0) {
        const formControl = this.directorsReportForm.get(control.qname) as FormControl;
        if (formControl) {
          const warnings = this.warningService.evaluateWarnings(
            formControl,
            control.warnings,
            allValuesForWarnings,
            true // Always evaluate warnings in Role-4
          );

          if (warnings.length > 0) {
            this.fieldWarnings[control.qname] = warnings;
          } else {
            // Clear warnings if none triggered
            delete this.fieldWarnings[control.qname];
          }
        }
      }
    });

  }

  /**
   * Get warnings for a specific field
   */
  getFieldWarnings(qname: string): WarningResult[] {
    return this.fieldWarnings[qname] || [];
  }

  /**
   * Check if a field has warnings
   */
  hasWarnings(qname: string): boolean {
    return this.fieldWarnings[qname]?.length > 0;
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => {
      // Map severity to 'warning' | 'error' (treat 'info' as 'warning')
      const severity: 'warning' | 'error' = warning.severity === 'error' ? 'error' : 'warning';
      warnings.push({ message: warning.message, severity });
    });

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  /**
   * Split warning message to display red asterisk for (*)
   * Example: "Date (*) must be..." -> ["Date ", "(", "*", ")", " must be..."]
   */
  splitWarningMessage(message: string): { text: string; isAsterisk: boolean }[] {
    const parts: { text: string; isAsterisk: boolean }[] = [];
    const regex = /\(\*\)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(message)) !== null) {
      // Add text before the (*)
      if (match.index > lastIndex) {
        parts.push({
          text: message.substring(lastIndex, match.index),
          isAsterisk: false
        });
      }
      // Add the parts of (*) separately
      parts.push({ text: '(', isAsterisk: false });
      parts.push({ text: '*', isAsterisk: true });
      parts.push({ text: ')', isAsterisk: false });
      lastIndex = regex.lastIndex;
    }

    // Add remaining text after last (*)
    if (lastIndex < message.length) {
      parts.push({
        text: message.substring(lastIndex),
        isAsterisk: false
      });
    }

    return parts;
  }

  /**
   * Setup conditional pattern validators
   * Watches "Type of Identification" fields and dynamically applies/removes pattern validators
   * based on whether the type is MyKad/MyPR/MyKAS
   */
  private setupConditionalValidators(): void {
    // Director field triplets shared with role4DirectorsReportValidator via
    // ROLE4_DIRECTOR_MAPPINGS so the editor and the pure validator iterate
    // the same set.
    ROLE4_DIRECTOR_MAPPINGS.forEach(mapping => {
      const typeControl = this.directorsReportForm.get(mapping.typeField) as FormControl;
      const idControl = this.directorsReportForm.get(mapping.idField) as FormControl;

      if (typeControl && idControl) {
        // Apply initial validators based on current value
        this.applyConditionalValidator(typeControl.value, idControl);

        // Watch for changes
        typeControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(typeValue => {
            // Reset the identification number field when type changes
            idControl.setValue('', { emitEvent: false });

            // Apply the appropriate validator
            this.applyConditionalValidator(typeValue, idControl);

            // Mark as touched to show validation errors (required field is now empty)
            // This ensures the red border and error message remain visible
            if (typeValue) {
              idControl.markAsTouched();
            } else {
              // Type was CLEARED. Let validity decide how the paired field
              // renders: a VISIBLE director's identification number is still
              // `required`, so its error stays on screen; a HIDDEN director's
              // has no validators left, so it goes back to looking untouched —
              // which was the whole point of the original 2026-07-29 reset.
              //
              // Blanket-untouching both cases is what left a mandatory field
              // silent while it went on blocking submission (P.O. 2026-08-24:
              // a mandatory field shows its error however it came to be empty).
              // Role 5's setupAutoResetWatchers carries the same call.
              idControl.updateValueAndValidity({ emitEvent: false });
              syncRevealToValidity(idControl);
            }
          });
      }
    });
  }

  /**
   * Apply or remove pattern validator based on identification type
   * @param typeValue - The selected type of identification (e.g., "MyKad", "Passport")
   * @param idControl - The form control for the identification number field
   */
  private applyConditionalValidator(typeValue: string, idControl: FormControl): void {
    const requiresPattern = role4TypeRequiresPattern(typeValue);

    // Find which director this is for
    const directorNumber = this.getDirectorNumberForControl(idControl);

    // Check if this director is currently visible (required). A cleared count
    // resolves to 0 — no director is visible, so nothing here stays required.
    const isVisible = directorNumber <= this.getSelectedDirectorCount();

    // The FREE-TEXT ceiling from the schema (255). It is deliberately NOT
    // tightened to 12 for a numeric type: the pattern below already owns that
    // rule, and seating both would raise two messages for one mistake. The
    // rendered `maxlength` attribute still narrows to 12 (identification-field.util),
    // which caps typing rather than validating.
    const idFieldQname = ROLE4_DIRECTOR_MAPPINGS.find(
      (mapping) => mapping.directorNumber === directorNumber
    )?.idField;
    const maxLength = idFieldQname ? getFieldMaxLength(idFieldQname, this.schema) : null;
    const capValidators: ValidatorFn[] =
      isVisible && maxLength !== null ? [Validators.maxLength(maxLength)] : [];

    if (requiresPattern) {
      // Add pattern validator (12 digits)
      if (isVisible) {
        // Visible directors are required
        idControl.setValidators([Validators.required, Validators.pattern(ROLE4_IDENTIFICATION_PATTERN)]);
      } else {
        // Hidden directors only have pattern validator (no required)
        idControl.setValidators([Validators.pattern(ROLE4_IDENTIFICATION_PATTERN)]);
      }
    } else {
      // Remove pattern validator (keep only required + the cap if visible)
      if (isVisible) {
        // Visible directors remain required
        idControl.setValidators([Validators.required, ...capValidators]);
      } else {
        // Hidden directors have no validators
        idControl.clearValidators();
      }
    }

    // Update validity
    idControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Get director number for a given identification control
   */
  private getDirectorNumberForControl(idControl: FormControl): number {
    // Find the control in the form group
    for (const [key, control] of Object.entries(this.directorsReportForm.controls)) {
      if (control === idControl) {
        // Extract director number from qname
        if (key.includes('First')) return 1;
        if (key.includes('Second')) return 2;
        if (key.includes('Third')) return 3;
        if (key.includes('Fourth')) return 4;
        if (key.includes('Fifth')) return 5;
      }
    }
    return 0;
  }

  /**
   * Type 2: Check if field should be hidden (not rendered)
   * Returns true if field should be completely hidden based on schema's hideWhen configuration
   *
   * For inherited fields: Also checks if field exists in Role-1 (filingInfo)
   */
  shouldHideField(fieldQname: string): boolean {
    const control = this.schema.controls.find((control) => control.qname === fieldQname);
    if (!control) {
      return false;
    }

    // Read from the active filing-info source for the current phase
    // (filingPrerequisites in Phase 1, filingInfo in Phase 2). See PR-F.
    const filingInfoData = this.stateService.getActiveFilingInfoData();

    // For inherited fields: Hide if field doesn't exist or is empty upstream
    if (control.ui.inheritFrom === 'filingInfo') {
      const role1Value = filingInfoData?.[fieldQname];

      // Hide if field is not present or is empty/null/undefined in Role-1
      if (
        role1Value === undefined ||
        role1Value === null ||
        role1Value === ''
      ) {
        return true;
      }
    }

    // Schema-driven hideWhen logic
    if (control.ui.hideWhen) {
      const { field, values } = control.ui.hideWhen;
      const triggerValue = filingInfoData?.[field];

      if (values.includes(triggerValue)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a field has a meaningful value (not null, undefined, or empty string)
   */
  private fieldHasValue(control: FormControlSchema, allFormData: any): boolean {
    // Handle dimensional fields
    if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
      return control.ContextPolicy.allowedAxes.some(axis => {
        const fieldName = `${control.qname}[${axis}]`;
        const value = allFormData[fieldName];
        return value !== null && value !== undefined && value !== '';
      });
    }

    // Handle regular fields
    const value = allFormData[control.qname];
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * Get all visible fields for submission
   * Returns all visible fields based on schema visibility rules
   * Filters out unused fields (CLBG, scope-specific, etc.) based on submission type
   * CRITICAL: Transforms frontend bracket notation to backend nested format
   */
  getVisibleFormData(): any {
    const allFormData = this.directorsReportForm.getRawValue();
    const submissionData: any = {};

    // Dynamically collect all visible fields based on schema
    this.schema.controls.forEach((control: FormControlSchema) => {
      // Skip fields that should be hidden (based on hideWhen rules or inherited fields not in Role-0)
      if (this.shouldHideField(control.qname)) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⏭️ Skipping hidden field: ${control.qname}`);
        return;
      }

      // Check conditional visibility (requiredIf)
      if (control.requiredIf) {
        const dependentControl = this.directorsReportForm.get(control.requiredIf.qname);
        if (dependentControl?.value !== control.requiredIf.equals) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⏭️ Skipping conditionally hidden field: ${control.qname}`);
          return;
        }
      }

      // Check conditional visibility (requiredIfAny)
      if (control.requiredIfAny) {
        const shouldShow = control.requiredIfAny.some((condition) => {
          const dependentControl = this.directorsReportForm.get(condition.qname);
          return dependentControl?.value === condition.equals;
        });
        if (!shouldShow) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`⏭️ Skipping conditionally hidden field: ${control.qname}`);
          return;
        }
      }

      // Skip fields with empty values (optional optimization)
      // This reduces payload size by not sending empty optional fields
      const hasValue = this.fieldHasValue(control, allFormData);
      if (!control.required && !hasValue) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`⏭️ Skipping empty optional field: ${control.qname}`);
        return;
      }

      // Handle fields with dimensional axes (e.g., MSICCode with business members)
      if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
        control.ContextPolicy.allowedAxes.forEach(axis => {
          const fieldName = `${control.qname}[${axis}]`;
          if (allFormData.hasOwnProperty(fieldName)) {
            submissionData[fieldName] = allFormData[fieldName];
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log(`✅ Including dimensional field: ${fieldName} = ${allFormData[fieldName]}`);
          }
        });
      } else {
        // Regular field without axes
        const fieldName = control.qname;
        if (allFormData.hasOwnProperty(fieldName)) {
          submissionData[fieldName] = allFormData[fieldName];
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`✅ Including field: ${fieldName}`);
        }
      }
    });

    return submissionData;
  }

  /**
   * Handle cancel confirmed event from submit-for-validation component
   * Reloads saved state to properly revert all role-specific transformations
   * (TypeOfIdentification conversion, director field validators, etc.)
   */
  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.directorsReportForm.markAsPristine();
    void this.finalizeInit();
  }

  onInputChange(event: InputEvent) {
    handleDateInputChange(event, this.directorsReportForm);
  }
}
